/* eslint-disable no-template-curly-in-string */
/**
 * Настройка Monaco Editor для PlantUML и JavaScript
 */

import { builtinMetamodelContext } from './metamodelContext';

// Глобальное хранилище для сущностей из реестра ИС
let registryEntitiesCache = new Map();

export const updateRegistryEntities = (entitiesMap) => {
  registryEntitiesCache = entitiesMap;
};

// Structurizr DSL registry cache (separate from PlantUML cache)
let structurizrEntitiesCache = new Map();

export const updateStructurizrRegistryEntities = (entitiesMap) => {
  structurizrEntitiesCache = entitiesMap;
};

// State for free-dsl language (populated on first setupMonacoEditor call)
let _freeDslMonaco = null;
let _currentFreeDslCtx = null;

function buildMonarchConfig(ctx) {
  const keywords = Array.from(ctx.keywords.keys());
  return {
    keywords,
    tokenizer: {
      root: [
        [/^\s*[#'].*$/, 'comment'],
        [/[@](?:startfree|endfree)/, 'keyword.control'],
        [/\bas\b/, 'keyword.control'],
        [/<-->|<->|<--|<\.\.|<-|-->|\.\.>|->/, 'operator'],
        [/"[^"]*"/, 'string'],
        [/(:)(.*)$/, ['delimiter.colon', 'message.text']],
        [/[A-Za-z_][\w-]*/, { cases: { '@keywords': 'keyword', '@default': 'identifier' } }],
      ],
    },
  };
}

export function setActiveMetamodelContext(ctx) {
  _currentFreeDslCtx = ctx ?? builtinMetamodelContext();
  if (_freeDslMonaco) {
    _freeDslMonaco.languages.setMonarchTokensProvider('free-dsl', buildMonarchConfig(_currentFreeDslCtx));
  }
}

export const setupMonacoEditor = (monaco) => {
  suppressMonacoHitTestError();
  setupPlantUMLLanguage(monaco);
  setupStructurizrLanguage(monaco);
  setupFreeDslLanguage(monaco);
  setupCustomThemes(monaco);
  setupTypeScriptDefinitions(monaco);
};

/**
 * Monaco v0.52 bug: caretPositionFromPoint() returns null when the pointer
 * leaves the editor during text selection, and Monaco's _doHitTestWithCaretPositionFromPoint
 * accesses .offsetNode without a null-check, throwing "can't access property 'offsetNode', L is null".
 * Suppress this specific harmless error via a one-time global error listener.
 */
const suppressMonacoHitTestError = () => {
  if (window.__monacoHitTestErrorSuppressed) return;
  window.__monacoHitTestErrorSuppressed = true;
  window.addEventListener('error', (e) => {
    const stack = e.error?.stack || '';
    if (
      e.message &&
      e.message.includes('offsetNode') &&
      (stack.includes('_doHitTestWithCaretPositionFromPoint') || stack.includes('doHitTest') || stack.includes('caretPositionFromPoint'))
    ) {
      e.stopImmediatePropagation();
      e.preventDefault();
      return true;
    }
  }, true);
};

const setupPlantUMLLanguage = (monaco) => {
  if (window.__plantumlRegistered) return;

  window.__plantumlRegistered = true;

  // Регистрация языка PlantUML
  monaco.languages.register({ id: 'plantuml' });

  // Настройка токенизатора
  monaco.languages.setMonarchTokensProvider('plantuml', {
    tokenizer: {
      root: [
        // Comments
        [/^\s*'.*$/, 'comment'],
        // Keywords (declarations + control flow)
        [/\b(participant|database|actor|activate|deactivate|note|alt|opt|else|end|endif|as)\b/, 'keyword'],
        // Arrows
        [/--?>|<--?|->|<-/, 'operator'],
        // Colon separator + message text (colon gets delimiter token, rest gets message.text)
        [/(:)(.*)$/, ['delimiter.colon', 'message.text']],
        // Quoted strings (aliases like "My Service")
        [/"[^"]*"/, 'string'],
        // Identifiers (participant codes, actor names)
        [/\b[a-zA-Z_][a-zA-Z0-9_\-]*\b/, 'identifier']
      ]
    }
  });

  // Настройка автокомплита
  monaco.languages.registerCompletionItemProvider('plantuml', {
    triggerCharacters: [' ', '-', '>', '<', ':'],
    provideCompletionItems: (model, position) => {
      const lines = model.getValue().split(/\r?\n/);
      const currentLine = lines[position.lineNumber - 1] || '';
      const textBeforeCursor = currentLine.substring(0, position.column - 1);

      // Item 5: After ':' in a message line — suppress all keyword/participant suggestions
      if (/^\s*\S+\s*(-{1,2}>|<-{1,2})\s*\S+\s*:/.test(textBeforeCursor)) {
        return { suggestions: [] };
      }

      const participants = new Set();
      const partRe = /^\s*participant\s+(\S+)/i;
      const dbRe = /^\s*database\s+(\S+)/i;
      const actorRe = /^\s*actor\s+(\S+)/i;

      lines.forEach(l => {
        let m;
        if ((m = partRe.exec(l))) {
          participants.add(m[1]);
        } else if ((m = dbRe.exec(l))) {
          participants.add(m[1]);
        } else if ((m = actorRe.exec(l))) {
          participants.add(m[1]);
        }
      });

      const word = model.getWordUntilPosition(position).word || '';
      const lc = word.toLowerCase();
      const Kind = monaco.languages.CompletionItemKind;
      const SnipRule = monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet;

      const list = [];

      // Если строка начинается с participant или database - показываем коды из реестра
      if (/^\s*participant\s+\S*$/i.test(textBeforeCursor) || /^\s*participant$/i.test(textBeforeCursor)) {
        // Показываем все сущности для participant
        registryEntitiesCache.forEach((entity, code) => {
          list.push({
            label: code,
            kind: Kind.Value,
            detail: `${entity.name} (${entity.type === 'system' ? 'Система' : 'Модуль'})`,
            documentation: entity.description || '',
            insertText: `${code} as "${entity.name}"`,
            sortText: `1_${code}`
          });
        });
      } else if (/^\s*database\s+\S*$/i.test(textBeforeCursor) || /^\s*database$/i.test(textBeforeCursor)) {
        // Показываем только БД для database
        registryEntitiesCache.forEach((entity, code) => {
          if (entity.model === 'db') {
            list.push({
              label: code,
              kind: Kind.Value,
              detail: `${entity.name} (Database)`,
              documentation: entity.description || '',
              insertText: `${code} as "${entity.name}"`,
              sortText: `1_${code}`
            });
          }
        });
      } else if (/^\s*alt\b/i.test(textBeforeCursor) || /^\s*opt\b/i.test(textBeforeCursor)) {
        // ничего не подсказываем специфичного, оставим общие ключевые слова ниже
      } else if (/^\s*else\b/i.test(textBeforeCursor)) {
        // общие ключевые слова
      } else if (/^\s*\S+\s*(-{1,2}>?|<-{1,2}?)\s*\S*$/i.test(textBeforeCursor)) {
        // В сообщениях подсказываем существующих участников + реестр
        registryEntitiesCache.forEach((entity, code) => {
          list.push({
            label: code,
            kind: Kind.Reference,
            detail: entity.name,
            insertText: code,
            sortText: `1_${code}`
          });
        });
        participants.forEach(p => {
          if (!list.find(i => i.label === p)) {
            list.push({
              label: p,
              kind: Kind.Reference,
              insertText: p,
              sortText: `2_${p}`
            });
          }
        });
      } else {
        // Обычные подсказки ключевых слов
        list.push(
          { label: 'participant', kind: Kind.Keyword, insertText: 'participant', sortText: 'a_participant' },
          { label: 'participant with templ', kind: Kind.Snippet, insertText: 'participant ${1:Code} as "${2:Name}"', insertTextRules: SnipRule, sortText: 'a_participant_tmpl' },
          { label: 'database', kind: Kind.Keyword, insertText: 'database', sortText: 'a_database' },
          { label: 'database with templ', kind: Kind.Snippet, insertText: 'database ${1:Code} as "${2:Name}"', insertTextRules: SnipRule, sortText: 'a_database_tmpl' },
          // Item 3: actor keyword + snippet
          { label: 'actor', kind: Kind.Keyword, insertText: 'actor', sortText: 'a_actor' },
          { label: 'actor with templ', kind: Kind.Snippet, insertText: 'actor ${1:ActorName}', insertTextRules: SnipRule, sortText: 'a_actor_tmpl' },
          { label: 'activate', kind: Kind.Keyword, insertText: 'activate ${1:Actor}', sortText: 'b_activate' },
          { label: 'deactivate', kind: Kind.Keyword, insertText: 'deactivate ${1:Actor}', sortText: 'b_deactivate' },
          { label: 'message', kind: Kind.Snippet, insertText: '${1:From} -> ${2:To}: ${3:Label}', insertTextRules: SnipRule, sortText: 'c_message' },
          { label: 'message dashed', kind: Kind.Snippet, insertText: '${1:From} --> ${2:To}: ${3:Label}', insertTextRules: SnipRule, sortText: 'c_message_dashed' },
          { label: 'note right', kind: Kind.Snippet, insertText: 'note right of ${1:Actor}: ${2:Text}\nend note', insertTextRules: SnipRule, sortText: 'd_note' },
          { label: 'note left', kind: Kind.Snippet, insertText: 'note left of ${1:Actor}: ${2:Text}\nend note', insertTextRules: SnipRule, sortText: 'd_note' },
          { label: 'alt', kind: Kind.Snippet, insertText: 'alt ${1:condition}\n  ${2:Actor1} -> ${3:Actor2}: ${4:Message}\nelse ${5:other}\n  ${6:Actor1} -> ${7:Actor2}: ${8:Message}\nend', insertTextRules: SnipRule, sortText: 'e_alt' },
          { label: 'opt', kind: Kind.Snippet, insertText: 'opt ${1:condition}\n  ${2:Actor1} -> ${3:Actor2}: ${4:Message}\nend', insertTextRules: SnipRule, sortText: 'e_opt' },
          { label: 'else', kind: Kind.Keyword, insertText: 'else ${1:other}', sortText: 'f_else' },
          { label: 'end', kind: Kind.Keyword, insertText: 'end', sortText: 'f_end' }
        );

        participants.forEach(p => list.push({ label: p, kind: Kind.Reference, insertText: p, sortText: `g_${p}` }));
      }

      const filtered = word ? list.filter(i => i.label.toLowerCase().startsWith(lc)) : list;
      return { suggestions: filtered };
    }
  });
};

const setupStructurizrLanguage = (monaco) => {
  if (window.__structurizrRegistered) return;
  window.__structurizrRegistered = true;

  const Kind = monaco.languages.CompletionItemKind;
  const SnipRule = monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet;

  monaco.languages.registerCompletionItemProvider('hcl', {
    triggerCharacters: [' ', '\n', '='],
    provideCompletionItems: (model, position) => {
      const lines = model.getValue().split(/\r?\n/);
      const textBeforeCursor = (lines[position.lineNumber - 1] || '').substring(0, position.column - 1);
      const word = model.getWordUntilPosition(position).word || '';
      const lc = word.toLowerCase();

      const list = [];

      // ── Registry entity suggestions after element-type keywords ──────────
      const afterKeyword = /\b(softwareSystem|container|component)\s+\S*$/i.exec(textBeforeCursor);
      if (afterKeyword) {
        structurizrEntitiesCache.forEach((entity, code) => {
          list.push({
            label: code,
            kind: Kind.Value,
            detail: entity.name,
            documentation: entity.description || '',
            insertText: `"${entity.name}"`,
            sortText: `1_${code}`,
          });
        });
        const filtered = word ? list.filter(i => i.label.toLowerCase().startsWith(lc)) : list;
        return { suggestions: filtered };
      }

      // ── Keyword and snippet suggestions ──────────────────────────────────
      list.push(
        { label: 'workspace',     kind: Kind.Keyword,  insertText: 'workspace', sortText: 'a_workspace' },
        { label: 'workspace {}',  kind: Kind.Snippet,  insertText: 'workspace {\n  model {\n    $0\n  }\n  views {\n  }\n}', insertTextRules: SnipRule, sortText: 'a_workspace_snip' },
        { label: 'model',         kind: Kind.Keyword,  insertText: 'model', sortText: 'b_model' },
        { label: 'views',         kind: Kind.Keyword,  insertText: 'views', sortText: 'b_views' },
        { label: 'person',        kind: Kind.Keyword,  insertText: 'person', sortText: 'c_person' },
        { label: 'person {}',     kind: Kind.Snippet,  insertText: '${1:id} = person "${2:Name}" "${3:Description}"', insertTextRules: SnipRule, sortText: 'c_person_snip' },
        { label: 'softwareSystem',kind: Kind.Keyword,  insertText: 'softwareSystem', sortText: 'c_ss' },
        { label: 'softwareSystem {}', kind: Kind.Snippet, insertText: '${1:id} = softwareSystem "${2:Name}" "${3:Description}"', insertTextRules: SnipRule, sortText: 'c_ss_snip' },
        { label: 'container',     kind: Kind.Keyword,  insertText: 'container', sortText: 'c_container' },
        { label: 'container {}',  kind: Kind.Snippet,  insertText: '${1:id} = container "${2:Name}" "${3:Description}" "${4:Technology}"', insertTextRules: SnipRule, sortText: 'c_container_snip' },
        { label: 'component',     kind: Kind.Keyword,  insertText: 'component', sortText: 'c_component' },
        { label: 'component {}',  kind: Kind.Snippet,  insertText: '${1:id} = component "${2:Name}" "${3:Description}" "${4:Technology}"', insertTextRules: SnipRule, sortText: 'c_component_snip' },
        { label: 'systemContext', kind: Kind.Keyword,  insertText: 'systemContext', sortText: 'd_sc' },
        { label: 'systemContext {}', kind: Kind.Snippet, insertText: 'systemContext ${1:systemId} {\n  include *\n}', insertTextRules: SnipRule, sortText: 'd_sc_snip' },
        { label: 'containerView', kind: Kind.Keyword,  insertText: 'containerView', sortText: 'd_cv' },
        { label: 'containerView {}', kind: Kind.Snippet, insertText: 'containerView ${1:systemId} {\n  include *\n}', insertTextRules: SnipRule, sortText: 'd_cv_snip' },
        { label: 'componentView', kind: Kind.Keyword,  insertText: 'componentView', sortText: 'd_compv' },
        { label: 'include *',     kind: Kind.Snippet,  insertText: 'include *', sortText: 'e_include' },
        { label: '->',            kind: Kind.Operator, insertText: '${1:from} -> ${2:to} "${3:label}"', insertTextRules: SnipRule, sortText: 'f_rel' },
      );

      const filtered = word ? list.filter(i => i.label.toLowerCase().startsWith(lc)) : list;
      return { suggestions: filtered };
    },
  });
};

const setupFreeDslLanguage = (monaco) => {
  // Bump version key to force re-registration after HMR with stale guard
  if (window.__freeDslRegistered_v4) return;
  window.__freeDslRegistered_v4 = true;

  _freeDslMonaco = monaco;
  if (!_currentFreeDslCtx) _currentFreeDslCtx = builtinMetamodelContext();

  monaco.languages.register({ id: 'free-dsl' });

  // Standard Monarch pattern: keywords list + @keywords cases.
  // @keywords in a 'cases' block is safe — it's only @name in regex patterns that
  // triggers Monarch attribute lookup and would throw.
  monaco.languages.setMonarchTokensProvider('free-dsl', buildMonarchConfig(_currentFreeDslCtx));

  const ARROWS = ['->', '-->', '..>', '<-', '<--', '<-->', '<->'];

  monaco.languages.registerCompletionItemProvider('free-dsl', {
    triggerCharacters: [' '],
    provideCompletionItems: (model, position) => {
      const word = model.getWordUntilPosition(position);
      const range = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: word.startColumn,
        endColumn: word.endColumn,
      };

      const Kind = monaco.languages.CompletionItemKind;
      const SnipRule = monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet;

      // Text on current line before the cursor, leading whitespace stripped
      const before = model
        .getValueInRange({
          startLineNumber: position.lineNumber,
          startColumn: 1,
          endLineNumber: position.lineNumber,
          endColumn: position.column,
        })
        .trimStart();

      // After `TYPE [partial_id]` (keyword with registrySlug, no second space yet) → suggest entry codes
      const kwMatch = before.match(/^([A-Za-z_][\w-]*)\s+([^\s"]*)$/);
      if (kwMatch) {
        const kw = _currentFreeDslCtx?.keywords.get(kwMatch[1]);
        if (kw?.registrySlug && _currentFreeDslCtx.registryEntries) {
          const codes = _currentFreeDslCtx.registryEntries.get(kw.registrySlug);
          if (codes?.size) {
            return {
              suggestions: Array.from(codes).map(code => ({
                label: code,
                kind: Kind.Value,
                insertText: code,
                range,
                detail: `реестр: ${kw.registrySlug}`,
              })),
            };
          }
        }
      }

      // After `TYPE ID ` or `TYPE "Quoted" ` on a declaration line → suggest `as` + arrows
      if (/^[A-Za-z_][\w-]*\s+(?:"[^"]*"|[A-Za-z_][\w-]*)\s+$/.test(before)) {
        return {
          suggestions: [
            {
              label: 'as',
              kind: Kind.Keyword,
              insertText: 'as "${1:Display Name}"',
              insertTextRules: SnipRule,
              detail: 'display label',
              range,
            },
            ...ARROWS.map(op => ({
              label: op,
              kind: Kind.Operator,
              insertText: op + ' ',
              range,
              detail: 'arrow',
            })),
          ],
        };
      }

      // After a bare shape ID (quoted or plain) followed by a space → suggest arrows only
      if (/^(?:"[^"]*"|[A-Za-z_][\w-]*)\s+$/.test(before)) {
        return {
          suggestions: ARROWS.map(op => ({
            label: op,
            kind: Kind.Operator,
            insertText: op + ' ',
            range,
            detail: 'arrow',
          })),
        };
      }

      // Default: shape keywords from active metamodel context (read dynamically)
      const suggestions = [];
      for (const k of _currentFreeDslCtx.keywords.keys()) {
        suggestions.push({
          label: k,
          kind: Kind.Keyword,
          insertText: `${k} \${1:${k.charAt(0).toUpperCase() + k.slice(1).replace(/_/g, '')}}`,
          insertTextRules: SnipRule,
          detail: 'shape type',
          range,
        });
      }
      return { suggestions };
    },
  });
};

const setupCustomThemes = (monaco) => {
  // Bump version key to force re-definition after guard was set by older code
  if (window.__customDarkThemeDefined_v2) return;

  window.__customDarkThemeDefined_v2 = true;

  monaco.editor.defineTheme('custom-dark', {
    base: 'vs-dark',
    inherit: true,
    rules: [
      { token: 'keyword',          foreground: 'C792EA' },  // purple — shape keywords, @startfree
      { token: 'keyword.control',  foreground: 'C792EA' },  // purple — @startfree / @endfree
      { token: 'operator',         foreground: '89DDFF' },  // cyan — arrows (-> -->)
      { token: 'string',           foreground: 'FFCB6B' },  // gold — quoted labels ("My Service")
      { token: 'message.text',     foreground: 'C3E88D' },  // green — label text after ':'
      { token: 'delimiter.colon',  foreground: 'EEFFFF' },  // white — ':' separator
      { token: 'comment',          foreground: '5C6370', fontStyle: 'italic' },
      { token: 'identifier',       foreground: 'EEFFFF' },  // white — shape IDs / participant codes
    ],
    colors: {
      'editor.background': '#1f2124',
      'editor.lineHighlightBackground': '#2a2d31',
      'editorLineNumber.foreground': '#4b525a',
      'editorLineNumber.activeForeground': '#c6cdd5',
      'editor.selectionBackground': '#2f4a72',
      'editorCursor.foreground': '#ffffff'
    }
  });
};

const setupTypeScriptDefinitions = (monaco) => {
  try {
    const dts = `declare const ElementFactory:{rectangle(x:number,y:number,width?:number,height?:number,props?:any):any;circle(x:number,y:number,radius?:number,props?:any):any;arrow(x1:number,y1:number,x2:number,y2:number,startConnection?:any,endConnection?:any):any;text(x:number,y:number,content?:string):any;};declare const Helpers:{textInside(el:any,content?:string,opt?:any):any;arrowBetween(a:any,b:any):any;};declare function findNearestConnectionPoint(x:number,y:number,els:any[],exclude?:any,maxDistance?:number):any;`;
    monaco.languages.typescript.javascriptDefaults.addExtraLib(dts, 'file:///env.d.ts');
  } catch {
    // ignore errors
  }
};
